/**
 * PowerPointPicker Component
 * Modal dialog to browse and select PowerPoint presentations from DAM
 * Features:
 * - Grid view of PowerPoint files
 * - Search/filter functionality
 * - File info display
 * - Select button to choose presentation
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Presentation,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  FileSpreadsheet,
} from 'lucide-react'
import { assetService } from '@/lib/api/services/assets'

// ============================================
// Types
// ============================================

export interface PowerPointPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (asset: {
    id: string
    url: string
    thumbnailUrl?: string
    name: string
    fileSize: number
    slideCount?: number
  }) => void
}

interface MockPresentation {
  id: string
  name: string
  url: string
  thumbnailUrl: string
  type: string
  size: number
  slideCount: number
  createdAt: string
  modifiedAt: string
}

// ============================================
// Mock Data for Development
// ============================================

const MOCK_PRESENTATIONS: MockPresentation[] = [
  {
    id: 'ppt-1',
    name: 'Q4 2025 Sales Report.pptx',
    url: '/assets/presentations/q4-sales-report.pptx',
    thumbnailUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200',
    type: 'Presentation',
    size: 2456780,
    slideCount: 24,
    createdAt: '2026-01-15T10:30:00Z',
    modifiedAt: '2026-01-20T14:00:00Z',
  },
  {
    id: 'ppt-2',
    name: 'Company Overview.pptx',
    url: '/assets/presentations/company-overview.pptx',
    thumbnailUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200',
    type: 'Presentation',
    size: 5678900,
    slideCount: 35,
    createdAt: '2026-01-10T09:00:00Z',
    modifiedAt: '2026-01-18T11:30:00Z',
  },
  {
    id: 'ppt-3',
    name: 'Product Launch Deck.pptx',
    url: '/assets/presentations/product-launch.pptx',
    thumbnailUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=200',
    type: 'Presentation',
    size: 3890000,
    slideCount: 18,
    createdAt: '2026-01-08T14:20:00Z',
    modifiedAt: '2026-01-15T16:45:00Z',
  },
  {
    id: 'ppt-4',
    name: 'Investor Pitch.pptx',
    url: '/assets/presentations/investor-pitch.pptx',
    thumbnailUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=200',
    type: 'Presentation',
    size: 4567890,
    slideCount: 28,
    createdAt: '2026-01-05T11:00:00Z',
    modifiedAt: '2026-01-12T09:15:00Z',
  },
  {
    id: 'ppt-5',
    name: 'Marketing Strategy 2026.pptx',
    url: '/assets/presentations/marketing-strategy.pptx',
    thumbnailUrl: 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=200',
    type: 'Presentation',
    size: 6789012,
    slideCount: 42,
    createdAt: '2026-01-03T08:30:00Z',
    modifiedAt: '2026-01-10T13:00:00Z',
  },
  {
    id: 'ppt-6',
    name: 'Team Training Slides.pptx',
    url: '/assets/presentations/team-training.pptx',
    thumbnailUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200',
    type: 'Presentation',
    size: 3456789,
    slideCount: 56,
    createdAt: '2026-01-01T10:00:00Z',
    modifiedAt: '2026-01-08T15:30:00Z',
  },
]

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'modifiedAt-desc' },
  { label: 'Oldest First', value: 'modifiedAt-asc' },
  { label: 'Name (A-Z)', value: 'name-asc' },
  { label: 'Name (Z-A)', value: 'name-desc' },
  { label: 'Most Slides', value: 'slideCount-desc' },
  { label: 'Fewest Slides', value: 'slideCount-asc' },
]

const PAGE_SIZE = 6

// ============================================
// PowerPointPicker Component
// ============================================

export function PowerPointPicker({
  open,
  onOpenChange,
  onSelect,
}: PowerPointPickerProps) {
  const [presentations, setPresentations] = useState<MockPresentation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('modifiedAt-desc')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Load presentations
  const loadPresentations = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500))

      // Try to load real assets first
      try {
        const response = await assetService.getAssets({
          top: 100,
          filter: [{ field: 'type', operator: 'eq', value: 'Presentation' }],
        })

        if (response.value && response.value.length > 0) {
          const realPresentations = response.value.map(asset => ({
            id: asset.id,
            name: asset.name,
            url: asset.url,
            thumbnailUrl: asset.thumbnailUrl || '',
            type: asset.type,
            size: asset.size,
            slideCount: 0, // Would need to extract from metadata
            createdAt: asset.createdAt,
            modifiedAt: asset.updatedAt || asset.createdAt,
          }))
          setPresentations(realPresentations)
          return
        }
      } catch {
        // API not available, use mock data
      }

      // Fall back to mock data
      setPresentations(MOCK_PRESENTATIONS)
    } catch (err) {
      setError('Failed to load presentations')
    } finally {
      setLoading(false)
    }
  }, [])

  // Load presentations when dialog opens
  useEffect(() => {
    if (open) {
      loadPresentations()
      setSelectedId(null)
      setSearch('')
      setPage(1)
    }
  }, [open, loadPresentations])

  // Filter and sort presentations
  const filteredPresentations = useMemo(() => {
    let result = [...presentations]

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(ppt =>
        ppt.name.toLowerCase().includes(searchLower)
      )
    }

    // Sort
    const [sortField, sortDir] = sortBy.split('-')
    result.sort((a, b) => {
      let comparison = 0
      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name)
      } else if (sortField === 'slideCount') {
        comparison = a.slideCount - b.slideCount
      } else if (sortField === 'modifiedAt') {
        comparison = new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime()
      }
      return sortDir === 'desc' ? -comparison : comparison
    })

    return result
  }, [presentations, search, sortBy])

  // Pagination
  const totalPages = Math.ceil(filteredPresentations.length / PAGE_SIZE)
  const paginatedPresentations = filteredPresentations.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  )

  // Handle select
  const handleSelect = useCallback(() => {
    const ppt = presentations.find(p => p.id === selectedId)
    if (ppt) {
      onSelect({
        id: ppt.id,
        url: ppt.url,
        thumbnailUrl: ppt.thumbnailUrl,
        name: ppt.name,
        fileSize: ppt.size,
        slideCount: ppt.slideCount,
      })
    }
  }, [selectedId, presentations, onSelect])

  // Format file size
  const formatFileSize = (bytes: number): string => {
    return assetService.formatFileSize(bytes)
  }

  // Format date
  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Presentation className="h-5 w-5 text-orange-500" />
            Select PowerPoint Presentation
          </DialogTitle>
          <DialogDescription>
            Browse and select a PowerPoint file from your asset library
          </DialogDescription>
        </DialogHeader>

        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-3 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search presentations..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="pl-10 rounded-xl"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] rounded-xl">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Presentation Grid */}
        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-1">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <Skeleton key={i} className="h-[140px] rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadPresentations}
                className="mt-4 rounded-lg"
              >
                Try Again
              </Button>
            </div>
          ) : paginatedPresentations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Presentation className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">
                {search ? 'No presentations match your search' : 'No presentations found'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-1">
              {paginatedPresentations.map((ppt) => (
                <button
                  key={ppt.id}
                  type="button"
                  onClick={() => setSelectedId(ppt.id)}
                  className={cn(
                    'relative rounded-xl overflow-hidden',
                    'border-2 transition-all text-left',
                    'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2',
                    'bg-white dark:bg-neutral-900',
                    selectedId === ppt.id
                      ? 'border-orange-500 ring-2 ring-orange-500 ring-offset-2'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                  )}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video relative bg-neutral-100 dark:bg-neutral-800">
                    {ppt.thumbnailUrl ? (
                      <img
                        src={ppt.thumbnailUrl}
                        alt={ppt.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.currentTarget
                          target.style.display = 'none'
                          const placeholder = target.nextElementSibling as HTMLElement
                          if (placeholder) placeholder.style.display = 'flex'
                        }}
                      />
                    ) : null}
                    <div
                      className={cn(
                        'absolute inset-0 items-center justify-center',
                        ppt.thumbnailUrl ? 'hidden' : 'flex'
                      )}
                    >
                      <FileSpreadsheet className="h-12 w-12 text-orange-400/50" />
                    </div>

                    {/* Selection Indicator */}
                    {selectedId === ppt.id && (
                      <div className="absolute top-2 right-2 bg-orange-500 rounded-full p-1">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}

                    {/* Slide Count Badge */}
                    <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-md">
                      {ppt.slideCount} slides
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="font-medium text-sm text-neutral-900 dark:text-white truncate">
                      {ppt.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      <span>{formatFileSize(ppt.size)}</span>
                      <span>•</span>
                      <span>{formatDate(ppt.modifiedAt)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Page {page} of {totalPages} ({filteredPresentations.length} presentations)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={page <= 1}
                className="rounded-lg"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages}
                className="rounded-lg"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedId}
            className="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Check className="h-4 w-4 mr-1.5" />
            Select Presentation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PowerPointPicker
